import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useState } from 'react';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { useSummarizeGoogleReviewMutation } from '../../../queries/GoogleBusiness/useSummarizeGoogleReviewMutation';
import { Button } from '../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../components-v2/Dialog';
import { MAX_CONTENT_LENGTH } from './constants';
import { styleSheet } from './styles';

interface ReviewSummarizeModalProps {
	open: boolean;
	reviewId: string;
	impersonationContext?: Api.IImpersonationContext;
	onSummarize: (summary: string) => void;
	onEditManually: () => void;
}

export const ReviewSummarizeModal = ({
	open,
	reviewId,
	impersonationContext,
	onSummarize,
	onEditManually,
}: ReviewSummarizeModalProps) => {
	const errorMessages = useErrorMessages();
	const [error, setError] = useState<string | null>(null);

	const { mutate: summarize, isPending } = useSummarizeGoogleReviewMutation({
		onSuccess: data => {
			if (data.summary) {
				onSummarize(data.summary);
			} else {
				setError('AI returned an empty summary. Please edit the review manually.');
			}
		},
		onError: err => {
			errorMessages.pushApiError(err);
			setError('Failed to summarize the review. Please try editing manually.');
		},
	});

	const handleSummarize = () => {
		setError(null);
		summarize({ reviewId, impersonationContext });
	};

	return (
		<Dialog open={open} dismissOnOutsideClick={false}>
			<DialogPopup styles={[styleSheet.summarizeDialogPopup]} showCloseButton={false}>
				<DialogHeader>
					<DialogTitle>Review is too long</DialogTitle>
					<DialogDescription>
						This review is over the {MAX_CONTENT_LENGTH}-character limit for the testimonial image. Longer reviews don't
						fit the design and end up too small to read. You can let AI shorten it, or edit it yourself.
					</DialogDescription>
				</DialogHeader>
				<DialogBody>
					{error && <p className={css(styleSheet.summarizeError)}>{error}</p>}
					<div className={css(styleSheet.summarizeActions)}>
						<Button onClick={handleSummarize} isLoading={isPending} disabled={isPending}>
							Summarize with AI
						</Button>
						<Button variant='secondary' onClick={onEditManually} disabled={isPending}>
							Edit Manually
						</Button>
					</div>
				</DialogBody>
			</DialogPopup>
		</Dialog>
	);
};
